import React, {useMemo, useState} from "react"
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";

import {
    useReactTable,
    getCoreRowModel,
} from '@tanstack/react-table'

import DataTable from "../../components/DataTable";
import FiltersBox from "../../components/FiltersBox";
import UserRoleAutocomplete from "../../components/inputs/UserRoleAutocomplete";
import WorkgroupsAutocomplete from "../../components/inputs/WorkgroupsAutocomplete";
import UserCol from "../../components/columns/UserCol";
import UserIsActiveAutocomplete from "../../components/inputs/UserIsActiveAutocomplete";
import GroupAutocomplete from "../../components/inputs/GroupAutocomplete";
import WorkgroupsCol from "../../components/columns/WorkgroupsCol";

import MDBadge from "materialDashboard/components/MDBadge";

import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Icon from "@mui/material/Icon";
import Grid from "@mui/material/Grid";
import ProvinceAutocomplete from "../../components/inputs/ProvinceAutocomplete";
import MunicipalityAutocomplete from "../../components/inputs/MunicipalityAutocomplete";
import UserConfirmedAtIsNullAutocomplete from "../../components/inputs/UserEmailConfirmedAutocomplete";


const UsersTable = (props) => {
    const {
        rows,
        page_count=-1,
        pagination,
        setPagination,
        onGlobalFilterChange,
        onFilterChange,
        filters,
        sorting,
        onSortingChange,
        dataTableId
    } = props;

    const {t} = useTranslation(["users", "enums"]);

    const columns = useMemo(
        () => [
            {
                accessorKey: 'user',
                cell: props => <UserCol user={props.row?.original} />,
                header: () => <span>{t("table.user")}</span>,
                enableSorting: true,
                footer: props => props.column.id,
                meta: {
                    width: "22rem"
                }
            },
            {
                accessorKey: 'workgroups',
                cell: props => <WorkgroupsCol workgroups={props.row?.original?.workgroups} />,
                header: () => <span>{t("table.workgroups")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    width: "7rem"
                }
            },
            {
                accessorKey: 'role',
                cell: info => t(`enums:role.${info.getValue()}`),
                header: () => <span>{t("table.role")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    width: "7rem"
                }
            },
            {
                accessorKey: 'groups',
                cell:  info => <GroupsCol groups={info.getValue()}/>,
                header: () => <span>{t("table.groups")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    width: "10rem"
                }
            },
            {
                accessorKey: 'status',
                cell: props => <StatusCol {...props.row.original} />,
                header: () => <span>{t("table.status")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    width: "8rem",
                    align: "center"
                }
            },
            {
                accessorKey: 'defaultLocale',
                cell: props => props.getValue(),
                header: () => <span>{t("table.defaultLocale")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    width: "7rem",
                    align: "center"
                }
            },
            {
                accessorKey: 'Actions',
                cell: props => <ActionCol row={props.row}/>,
                header: () => <span>{t("table.actions")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    align: "right"
                }
            },
        ],
        []  // eslint-disable-line react-hooks/exhaustive-deps
    )

    const defaultData = useMemo(() => [], [])

    const table = useReactTable({
        data: rows ?? defaultData,
        columns,
        pageCount: page_count ?? -1,
        state: {
            pagination,
            sorting,
        },
        onPaginationChange: setPagination,
        onGlobalFilterChange: onGlobalFilterChange,
        onSortingChange: onSortingChange,
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true,  // rows are expected to be paginated (controlled mode)
        manualSorting: true,  // rows are expected to be sorted (controlled mode)
        // getPaginationRowModel: getPaginationRowModel(), // If only doing manual pagination, you don't need this
        debugTable: true,
    })

    return <DataTable dataTableId={dataTableId}
                      table={table}
                      canSearch
                      showRowColoring={props.showRowColoring}
                      rowColoring={props.rowColoring}
                      setRowColoring={props.setRowColoring}
                      stickyHeader={true}
                      filtersEl={<Filters onChange={onFilterChange} initialValues={filters}/>}/>
}

const StatusCol = ({isActive, isConfirmed}) => {
    const isActiveContent = isActive ? "enabled" : "disabled";
    const isActiveColor = isActive ? "success" : "error";

    const isConfirmedContent = isConfirmed ? "confirmed" : "not confirmed";
    const isConfirmedColor = isConfirmed ? "success" : "error";

    return <Stack direction="row" spacing={1}>
        <MDBadge badgeContent={isActiveContent} color={isActiveColor} variant="gradient" size="sm" />
        <MDBadge badgeContent={isConfirmedContent} color={isConfirmedColor} variant="gradient" size="sm" />
    </Stack>
}

const ActionCol = ({row}) => {
    const navigate = useNavigate();
    const {id} = row.original;
    return <Stack direction="row" spacing={1} justifyContent="flex-end">
        <IconButton onClick={() => navigate(`/users/${id}`)}>
            <Icon fontSize="small">edit</Icon>
        </IconButton>
    </Stack>
}

const GroupsCol = ({groups}) => {
    return <Stack direction="row" spacing={1}>
        {groups.map(g => <MDBadge key={g.id} badgeContent={g.name} color="info" variant="gradient" size="sm" />)}
    </Stack>
}

const Filters = ({onChange, initialValues}) => {
    const {t} = useTranslation("users");

    const [selectedWorkgroups, setSelectedWorkgroups] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [role, setRole] = useState(null);
    const [statuses, setStatuses] = useState(initialValues?.is_active__in?.split(',') ?? []);
    const [provinceId, setProvinceId] = useState(initialValues.provinceId ?? null);
    const [municipalityId, setMunicipalityId] = useState(initialValues.municipalityId ?? null);
    const [confirmed, setConfirmed] = useState(initialValues.confirmed ?? null);

    const workgroupIds = selectedWorkgroups.map(w => w.id);

    const filters = {
        "role": role,
        "workgroup__id__in": workgroupIds.join(','),
        "group__id__in": selectedGroups.map(g => g.id).join(','),
        "is_active__in": statuses.join(','),
        "province_id": provinceId,
        "municipality_id": municipalityId,
        "confirmed_at__isnull": confirmed,
    }

    const handleOnApplyFilters = () => {
        onChange(filters);
    }

    const handleProvinceChange = (provinceId) => {
        setProvinceId(provinceId);
        setMunicipalityId(null);
    }

    return <FiltersBox onApplyFilters={handleOnApplyFilters}>
        <Grid container columnSpacing={2} rowSpacing={2}>
            <Grid item xs={12} md={4}>
                <WorkgroupsAutocomplete selectedWorkgroups={selectedWorkgroups}
                                        setSelectedWorkgroups={setSelectedWorkgroups}
                                        workgroupFilters={{
                                            acl__users_read: true
                                        }}
                                        fullWidth/>
            </Grid>
            <Grid item xs={12} md={4}>
                <GroupAutocomplete workgroupIds={workgroupIds}
                                   selectedGroups={selectedGroups}
                                   setSelectedGroups={setSelectedGroups}
                                   fullWidth />
            </Grid>
            <Grid item xs={12} md={4}>
                <UserRoleAutocomplete setUserRole={setRole} fullWidth/>
            </Grid>
            <Grid item xs={12} md={4}>
                <UserIsActiveAutocomplete selected={statuses} setSelected={setStatuses} fullWidth/>
            </Grid>
            <Grid item xs={12} md={4}>
                <ProvinceAutocomplete name="provinceId"
                                      value={provinceId}
                                      onChange={handleProvinceChange}
                                      MDInputProps={{
                                          name: 'provinceId',
                                          label: t('filters.provinceId'),
                                      }}/>
            </Grid>
            <Grid item xs={12} md={4}>
                <MunicipalityAutocomplete name="municipalityId"
                                          provinceId={provinceId}
                                          value={municipalityId}
                                          onChange={setMunicipalityId}
                                          MDInputProps={{
                                              name: 'municipalityId',
                                              label: t('filters.municipalityId'),
                                          }}/>
            </Grid>
            <Grid item xs={12} md={4}>
                <UserConfirmedAtIsNullAutocomplete selected={confirmed} setSelected={setConfirmed} fullWidth/>
            </Grid>
        </Grid>
    </FiltersBox>
}

export default UsersTable;